import { Alert, Box, Container, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type {
  ApprovalConfigurationVersion,
  CreateApprovalConfigurationVersionRequest,
} from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { ApprovalConfigurationContextProvider } from "./ApprovalConfigurationContext";
import ApprovalConfigurationPageHeader from "./ApprovalConfigurationPageHeader";
import type { ApprovalConfigurationState } from "./approvalConfigurationState";
import { validateApprovalStep } from "./approvalConfigurationState";
import ApprovalStepsSection from "./ApprovalStepsSection";
import EditApprovalStepSection from "./EditApprovalStepSection";
import GeneralApprovalSettingsSection from "./GeneralApprovalSettingsSection";
import useApprovalRuleOptions from "./useApprovalRuleOptions";

const createApprovalConfigurationVersion = withErrorHandling(adminApi.createApprovalConfigurationVersion);

const ApprovalConfigurationPage = () => {
  usePageTitle("Approval Configuration");

  const { versionId, activityId } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [state, setState] = useState<ApprovalConfigurationState>({});

  const hasEditPermissions = hasPermissions(["ManageWorkflows", "ViewOrganizationUsers"]);

  const getApprovalConfiguration = useCallback(async () => {
    const isNewConfig = searchParams.get("new") === "true";
    return adminApi.getApprovalConfiguration(defined(versionId), defined(activityId), {
      includeEmpty: isNewConfig,
    });
  }, [searchParams, versionId, activityId]);

  const [approvalConfig, fetchConfigError, { setData: setApprovalConfig }] = useFetch(getApprovalConfiguration);

  const {
    clientUserGroups,
    userGroupsError,
    isFetchingUserGroups,
    lookupOptionsByObjectType,
    lookupOptionsUnavailable,
  } = useApprovalRuleOptions(approvalConfig, state.editedStep, hasEditPermissions);

  if (!versionId || !activityId) {
    return <Navigate to={`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowTemplates}`} />;
  }

  const fetchError = fetchConfigError || userGroupsError;
  if (fetchError) {
    logError(fetchError, "[ApprovalConfigurationPage] getApprovalConfiguration");
    return <DataLoadingFailed />;
  }

  if (approvalConfig === undefined || isFetchingUserGroups) {
    return <InlineLoader />;
  }

  const handleConfigurationUpdated = (updatedApprovalConfig: ApprovalConfigurationVersion) => {
    setApprovalConfig(updatedApprovalConfig);

    setSearchParams(
      (prev) => {
        prev.delete("new");
        return prev;
      },
      { replace: true }
    );
  };

  const saveConfiguration = async (request: CreateApprovalConfigurationVersionRequest, successNotification: string) => {
    const [resp, error] = await createApprovalConfigurationVersion(
      approvalConfig.workflowTemplateVersionId,
      approvalConfig.workflowActivityId,
      request
    );

    if (error) {
      logError(error, "[ApprovalConfigurationPage] createApprovalConfigurationVersion");
      sendNotificationError(error.message);
      return false;
    }

    sendNotification(successNotification);
    handleConfigurationUpdated(resp);
    return true;
  };

  const editedStepValidationResults = validateApprovalStep(state, approvalConfig.steps);

  return (
    <ApprovalConfigurationContextProvider
      hasEditPermissions={hasEditPermissions}
      approvalConfiguration={approvalConfig}
      state={state}
      setState={setState}
      editedStepValidationResults={editedStepValidationResults}
      clientUserGroups={clientUserGroups}
      lookupOptionsByObjectType={lookupOptionsByObjectType}
      lookupOptionsUnavailable={lookupOptionsUnavailable}
      saveConfiguration={saveConfiguration}
    >
      <ApprovalConfigurationPageHeader />

      <ScrollableFlexContainer>
        <Container maxWidth="md" disableGutters sx={{ flex: 1 }}>
          {state.editedStep === undefined && (
            <Stack spacing={2} sx={{ py: 2.5 }}>
              <Typography variant="subtitle1">Approval Settings</Typography>
              {approvalConfig.steps.length === 0 && (
                <Alert severity="warning">
                  <Typography>
                    This configuration has no steps. Until steps are added, approval functionality will not be
                    available.
                  </Typography>
                </Alert>
              )}
              <GeneralApprovalSettingsSection />
              <Box sx={{ pt: 2 }}>
                <ApprovalStepsSection />
              </Box>
            </Stack>
          )}
          {state.editedStep !== undefined && (
            <Box sx={{ py: 2.5 }}>
              <EditApprovalStepSection />
            </Box>
          )}
        </Container>
      </ScrollableFlexContainer>
    </ApprovalConfigurationContextProvider>
  );
};

export default ApprovalConfigurationPage;
